import { useCallback } from 'react';

import {
  AchRepository,
  AchTransfer,
  AdminRepository,
  AdminTransfer,
  BookRepository,
  BookTransfer,
  CreateAchTransfer,
  CreateBookTransfer,
  CreateFXQuoteRequest,
  CreateIntlWireTransfer,
  CreateRealtimeTransfer,
  CreateWireTransfer,
  CryptoRepository,
  CryptoTransfer,
  FxQuote,
  FxRateSheet,
  IntlWireRepository,
  IntlWireTransfer,
  RealtimeRepository,
  RealtimeTransfer,
  ReviewWireTransferRequest,
  TransferFilterParams,
  TransferListResponse,
  TransferRepository,
  WireDrawdownRepository,
  WireRepository,
  WireTransfer,
  transfers,
} from '~/repositories';
import { CheckRepository, CheckTransfer } from '~/repositories/Transfer/CheckRepository';
import { ApiError, WireDrawdownRequest } from '~/typings/API';

import { RepositoryMethod, createApiHook } from './utils/createApiHook';

export type TransferID =
  | 'ach'
  | 'wire'
  | 'book'
  | 'intl-wire'
  | 'check'
  | 'realtime'
  | 'wire-drawdown'
  | 'admin'
  | 'crypto';
export type TransferType =
  | AchTransfer
  | WireTransfer
  | BookTransfer
  | IntlWireTransfer
  | CheckTransfer
  | RealtimeTransfer
  | WireDrawdownRequest
  | AdminTransfer
  | CryptoTransfer;
type TransferResponse = (params: GetRequestType) => Promise<TransferType>;

export type TransferFilterType = TransferFilterParams & {
  page?: number;
};

const TransferMethods = new Map<TransferID, TransferResponse>([
  ['ach', AchRepository.get],
  ['wire', WireRepository.get],
  ['book', BookRepository.get],
  ['intl-wire', IntlWireRepository.get],
  ['check', CheckRepository.get],
  ['realtime', RealtimeRepository.get],
  ['wire-drawdown', WireDrawdownRepository.get],
  ['admin', AdminRepository.get],
  ['crypto', CryptoRepository.get],
]);

export const useTransfer = <T>(type: TransferID, triggerAutomatically: boolean = true) =>
  createApiHook<T, GetRequestType>(TransferMethods.get(type) as RepositoryMethod<T, GetRequestType>, {
    triggerAutomatically,
    triggerOnSessionStoreSubscribe: triggerAutomatically,
  });

export const useTransfers = createApiHook<TransferListResponse, TransferFilterType>(TransferRepository.getAll, {
  triggerAutomatically: true,
  triggerOnSessionStoreSubscribe: true,
  includeQueryParams: true,
  addQueryParamsToUrl: true,
  excludeQueryParams: ['page'],
});

export const useTransfersFetch = createApiHook<TransferListResponse, TransferFilterType>(TransferRepository.getAll);

export const useCreateACHTransfer = createApiHook<AchTransfer, CreateAchTransfer>(AchRepository.create);

export const useCreateWireTransfer = createApiHook<WireTransfer, CreateWireTransfer>(WireRepository.create);

export const useCreateBookTransfer = createApiHook<BookTransfer, CreateBookTransfer>(BookRepository.create);

export const useCreateIntlWireTransfer = createApiHook<IntlWireTransfer, CreateIntlWireTransfer>(
  IntlWireRepository.create
);

interface CheckTransferIdProps {
  onSuccess: (id: string) => void;
  onError: (message: string) => void;
}

export const useCheckTransferId = ({ onSuccess, onError }: CheckTransferIdProps) => {
  const handleSuccess = useCallback(
    (data: TransferType) => {
      const id = 'id' in data ? data.id : 'adminTransferId' in data ? data.adminTransferId : undefined;
      if (typeof id !== 'undefined') {
        onSuccess(id);
      }
    },
    [onSuccess]
  );

  const handleError = useCallback(
    (error: ApiError) => {
      console.info('Filter by ID', { error });

      if (onError) {
        onError('Please enter a valid Transfer ID');
      }
    },
    [onError]
  );

  const { createRequest: requestAchTransfer } = useTransfer<AchTransfer>(
    'ach',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestWireTransfer } = useTransfer<WireTransfer>(
    'wire',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestBookTransfer } = useTransfer<BookTransfer>(
    'book',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestIntlWireTransfer } = useTransfer<IntlWireTransfer>(
    'intl-wire',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestCheckTransfer } = useTransfer<CheckTransfer>(
    'check',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestWireDrawdown } = useTransfer<WireDrawdownRequest>(
    'wire-drawdown',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestCryptoTransfer } = useTransfer<CryptoTransfer>(
    'crypto',
    false
  )({ onSuccess: handleSuccess, onError: handleError });

  const checkTransferId = useCallback(
    (id: string) => {
      if (
        !transfers.some((transfer) => transfer.idString && id.startsWith(transfer.idString)) &&
        !id.startsWith('admt_') &&
        !id.startsWith('crtr_')
      ) {
        onError('Enter a valid Transfer ID');

        return;
      }

      if (id.startsWith('acht_')) {
        requestAchTransfer({ id });
      } else if (id.startsWith('wire_')) {
        requestWireTransfer({ id });
      } else if (id.startsWith('wdrw_')) {
        requestWireDrawdown({ id });
      } else if (id.startsWith('book_')) {
        requestBookTransfer({ id });
      } else if (id.startsWith('chkt_')) {
        requestCheckTransfer({ id });
      } else if (id.startsWith('swft_')) {
        requestIntlWireTransfer({ id });
      } else if (id.startsWith('crtr_')) {
        requestCryptoTransfer({ id });
      }
    },
    [
      requestAchTransfer,
      requestWireTransfer,
      requestBookTransfer,
      requestIntlWireTransfer,
      requestCheckTransfer,
      requestWireDrawdown,
      requestCryptoTransfer,
      onError,
    ]
  );

  return checkTransferId;
};

export const useFxRateSheet = createApiHook<FxRateSheet, unknown>(IntlWireRepository.getFxRateSheet);

export const useCreateFxQuote = createApiHook<FxQuote, CreateFXQuoteRequest>(IntlWireRepository.createFxQuote);

export const useCreateRealtimeTransfer = createApiHook<RealtimeTransfer, CreateRealtimeTransfer>(
  RealtimeRepository.create
);

export const useReviewAchTransfer = createApiHook<AchTransfer, ReviewWireTransferRequest>(AchRepository.review);

export const useReviewWireTransfer = createApiHook<WireTransfer, ReviewWireTransferRequest>(WireRepository.review);
